import type { AccionesRol } from 'modelos';

/**
 * Qué hace falta para **ver** cada pantalla de FARO.
 *
 * Es la única tabla que lo dice: la leen el menú, los guards de las rutas, el
 * buscador global y los enlaces entre detalles (un detalle de luminaria no
 * ofrece el link al gateway si el operador no puede entrar a los gateways). Si
 * cada lugar repitiera la acción, tarde o temprano uno se desalinea y el menú
 * esconde algo que el buscador sigue mostrando.
 *
 * Los requisitos son los mismos que IRIX usa en su menú
 * (`navigation.component.html`), acotados a lo que existe en FARO:
 *
 * - Luminarias, puestas, grupos, zonas, comandos, seguimiento, modelos de
 *   luminaria y perfiles no piden acción: en IRIX alcanza con el módulo de
 *   Luminarias, que en FARO ya exige `alumbradoGuard` para entrar a la app.
 * - Nodos y los catálogos de dispositivo piden `Ver dispositivos Lorawan`.
 * - La fila del network server es **sólo nivel 0**: es estado interno del NS
 *   y su botón borra downlinks. El backend la protege con `Nivel0Guard`.
 * - Configurar cliente pide `Configuraciones - Configurar cliente`; configurar
 *   el usuario propio no pide nada (como en IRIX).
 * - Logs, Mantenimiento y Administración piden además su módulo, con la regla
 *   de IRIX (`tieneModulo`): la bandera del permiso **y** el módulo activo en
 *   la configuración del cliente.
 */
export type ModuloFaro = 'Luminarias' | 'Logs' | 'Servicio Técnico' | 'Administración';

export interface Requisito {
  /** Acciones de rol. Con varias, alcanza una (igual que `tienePermiso`). */
  readonly acciones?: readonly AccionesRol[];
  /** Módulo que tiene que estar habilitado para el permiso y para el cliente. */
  readonly modulo?: ModuloFaro;
  /** Sólo para clientes de nivel 0. */
  readonly nivel0?: boolean;
  /** Sólo si el cliente trabaja con puestas (`config.moduloLuminarias.usaPuestas`). */
  readonly usaPuestas?: boolean;
}

export type VistaFaro =
  | 'panorama'
  | 'mapa'
  | 'eventos'
  | 'luminarias'
  | 'puestas'
  | 'gruposLuminarias'
  | 'gruposPuestas'
  | 'zonas'
  | 'comandos'
  | 'seguimiento'
  | 'nodos'
  | 'gateways'
  | 'filaNs'
  | 'botonesBle'
  | 'metricasDownlinks'
  | 'logs'
  | 'modelosLuminaria'
  | 'modelosDispositivo'
  | 'codigosDispositivo'
  | 'perfiles'
  | 'solicitudes'
  | 'materiales'
  | 'tecnicos'
  | 'clientes'
  | 'usuarios'
  | 'roles'
  | 'configurarCliente'
  | 'configurarUsuario';

export const VISTAS: Readonly<Record<VistaFaro, Requisito>> = {
  panorama: {},
  mapa: {},
  eventos: { acciones: ['Eventos - Ver eventos'] },

  luminarias: {},
  puestas: { usaPuestas: true },
  gruposLuminarias: {},
  gruposPuestas: { usaPuestas: true },
  zonas: {},

  comandos: {},
  seguimiento: {},

  nodos: { acciones: ['Administración - Ver dispositivos Lorawan'] },
  gateways: { acciones: ['Administración - Ver gateways'] },
  filaNs: { nivel0: true },
  botonesBle: { acciones: ['Administración - Ver botones Ble'] },
  // En IRIX las métricas y los logs están en el módulo Logs.
  metricasDownlinks: { modulo: 'Logs', acciones: ['Logs - Ver métricas downlinks'] },
  logs: { modulo: 'Logs', acciones: ['Logs - Ver logs eventos lora'] },

  modelosLuminaria: {},
  modelosDispositivo: { acciones: ['Administración - Ver dispositivos Lorawan'] },
  codigosDispositivo: { acciones: ['Administración - Ver dispositivos Lorawan'] },
  perfiles: {},

  solicitudes: { modulo: 'Servicio Técnico', acciones: ['Servicio Técnico - Ver solicitudes'] },
  materiales: {
    modulo: 'Servicio Técnico',
    acciones: ['Servicio Técnico - Ver pedidos de materiales'],
  },
  // Como IRIX: en su menú los técnicos cuelgan de Servicio Técnico.
  tecnicos: { modulo: 'Servicio Técnico', acciones: ['Servicio Técnico - Ver técnicos'] },

  clientes: { modulo: 'Administración', acciones: ['Administración - Ver clientes'] },
  usuarios: { modulo: 'Administración', acciones: ['Administración - Ver usuarios'] },
  roles: { modulo: 'Administración', acciones: ['Administración - Ver roles'] },

  // Como IRIX: configurar el cliente propio pide su acción y nada más (ni el
  // módulo de Administración); configurar el usuario propio, nada.
  configurarCliente: { acciones: ['Configuraciones - Configurar cliente'] },
  configurarUsuario: {},
};
